import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { ArrowRight } from "lucide-react";

/**
 * Slim compare-plans strip that sits directly under the landing
 * state-request banner on desktop. Complements the "$79.99 starting
 * dose" hook by pulling the live per-medication monthly prices from
 * `GET /api/pricing`, so the strip stays truthful when tier prices
 * change. Hidden on mobile viewports where the banner is also hidden.
 * Fails silent — renders nothing on error or while loading so we
 * never ship a half-baked comparison to the visitor.
 */
export default function LandingComparePlansStrip() {
  const [rows, setRows] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [priceR, flagsR, togglesR] = await Promise.all([
          API.get("/pricing"),
          API.get("/landing/flags").catch(() => null),
          API.get("/public/strip-toggles").catch(() => null),
        ]);
        if (cancelled) return;
        const active = (Array.isArray(priceR.data) ? priceR.data : [])
          .filter((r) => r?.active !== false)
          .sort((a, b) => (a.monthly_price || 0) - (b.monthly_price || 0));
        setRows(active);
        // Banners tab toggle wins; legacy landing/flags kept as fallback.
        const stripEnabled = togglesR?.data?.compare_plans_enabled;
        setEnabled(stripEnabled === undefined ? flagsR?.data?.compare_strip !== false : stripEnabled);
      } catch { /* silent — strip hides itself */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!enabled || !rows || rows.length === 0) return null;

  return (
    <div
      className="w-full bg-slate-950 text-white border-b border-white/10"
      data-testid="landing-compare-plans-strip"
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-1 sm:py-2 flex items-center gap-2 lg:gap-5 overflow-x-auto">
        <span className="text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-fuchsia-300 shrink-0">
          Compare
        </span>
        <ComparePill label="Starter dose" price="$79.99" note="single-vial trial" />
        {rows.map((r) => (
          <ComparePill
            key={r.drug_slug || r.id}
            label={r.drug_name}
            price={`$${r.monthly_price}/mo`}
            note={
              r.royal_tier_price && r.royal_tier_price < r.monthly_price
                ? `Royal saves $${r.monthly_price - r.royal_tier_price}/mo`
                : "Provider visit included"
            }
            testid={`compare-pill-${r.drug_slug}`}
          />
        ))}
        <Link
          to="/pricing"
          className="shrink-0 text-[10px] sm:text-xs font-bold text-fuchsia-300 hover:text-white inline-flex items-center gap-1 ml-auto"
          data-testid="compare-strip-see-all"
        >
          <span className="hidden sm:inline">See full pricing</span>
          <span className="sm:hidden">All</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function ComparePill({ label, price, note, testid }) {
  return (
    <div
      className="flex items-center gap-1 sm:gap-2 bg-white/5 border border-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md shrink-0"
      data-testid={testid || "compare-pill"}
    >
      <span className="text-[9px] sm:text-xs uppercase tracking-wider text-white/60 whitespace-nowrap">{label}</span>
      <span className="text-xs sm:text-sm font-extrabold text-white whitespace-nowrap">{price}</span>
      <span className="hidden lg:inline text-[11px] text-white/50">{note}</span>
    </div>
  );
}
